import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { useForm } from 'react-hook-form';
import { shallow } from 'zustand/shallow';
import { useSnackbar } from 'notistack';
import {
  cancelationReasonsExternalUser,
  cancelationReasonsInternalUser,
} from '@components/modals/CancelationModal/CancelationModal.constants';
import { useStore } from '@store/GlobalStore';
import { useCancelQuote } from '@services/quotes';
import { Dialog } from '@components/dialog';
import * as styles from '@components/modals/Modal.styles';
import { ToastType } from '@components/toast';
import { FormSection, Form } from '@components/forms';
import { getFormFields } from './getFormFields';
import { CancellationModalFormValues } from '@components/modals/CancelationModal/CancellationModalFormValues.type';

export type CancelationModalProps = {
  isOpen: boolean;
  policyNumber: string;
  quoteId: string;
  onCloseModal: () => void;
  onSuccess?: () => void;
};

export const CancelationModal: React.FC<CancelationModalProps> = ({
  isOpen,
  policyNumber,
  quoteId,
  onCloseModal,
  onSuccess,
}) => {
  const { cancelQuote, isPending: isLoading } = useCancelQuote();
  const isInternalUser = useStore((state) => state.userAuth.isInternalUser, shallow);

  const methods = useForm<CancellationModalFormValues>({
    defaultValues: {},
    mode: 'onChange',
  });
  const handleExit = () => {
    clearForm();
    onCloseModal();
  };

  const clearForm = () => {
    methods.reset({});
  };

  const { enqueueSnackbar } = useSnackbar();

  const handleCancelQuote = async () => {
    if (!quoteId) {
      return;
    }

    const formData = methods.getValues();
    const cancelQuoteBody = {
      quoteId,
      reason: formData.reason,
      description: formData.notes,
      details: {
        carrier: formData.carrier,
        otherCarrier: formData.otherCarrier,
        duplicate: formData.duplicateSource,
        premium: formData.premium,
      },
    };

    cancelQuote(cancelQuoteBody, {
      onError: () => {
        enqueueSnackbar(`Cancellation of quote #${policyNumber} has failed.`, {
          preventDuplicate: true,
          variant: ToastType.Error,
        });
      },
      onSuccess: () => {
        clearForm();
        onSuccess?.();
        onCloseModal();
        enqueueSnackbar(`Quote #${policyNumber} was successfully cancelled.`, {
          variant: ToastType.Success,
          preventDuplicate: true,
        });
      },
    });
  };

  const cancelationReasons = isInternalUser ? cancelationReasonsInternalUser : cancelationReasonsExternalUser;

  const fields = useMemo(() => {
    return getFormFields({ cancelationReasons });
  }, [cancelationReasons]);

  const isValid = methods.formState.isValid;

  return (
    <Dialog
      handleCloseDialog={handleExit}
      isPrimaryBtnDisabled={!isValid}
      isPrimaryBtnLoading={isLoading}
      open={isOpen}
      primaryBtnText='Cancel Quote'
      primaryBtnAction={handleCancelQuote}
      secondaryBtnAction={handleExit}
      secondaryBtnText='Exit'
      title={`Cancellation of Quote #${policyNumber}`}
    >
      <Form methods={methods} formId='cancellation-modal-form'>
        <Box sx={{ ...styles.modalContent, paddingTop: 0, paddingBottom: 0 }}>
          <Box sx={styles.modalScrollContainer}>
            <FormSection fields={fields} styleConfig={{ spacing: 3 }} />
          </Box>
        </Box>
      </Form>
    </Dialog>
  );
};
